import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { NpiVerificationModal } from '@/components/NpiVerificationModal';
import { AuthModal } from '@/components/authModal';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { communityShowAuthModalAtom, userIdAtom } from '@/services/globalState';

/**
 * The modal `useAbilityGate` opens when it turns a reader away: sign-in for a
 * guest, NPI verification for anyone signed in who still needs it. The gate
 * routes an account still confirming its details to that screen instead.
 *
 * Both modals share one open flag, and which one renders is decided here, at
 * render time, from whether a user id exists. So a flag raised for a guest
 * outlives a sign-in that happens inside the sign-in modal, and without a check
 * the same flag then opens NPI verification for the reader who just signed in,
 * verified or not. NPI verification therefore opens only while the account
 * actually needs it, and a flag left up for a signed-in reader who does not is
 * lowered once their account has loaded, so it cannot resurface later as a
 * sign-in modal after sign-out. While the account is still loading the flag is
 * left alone, since `needsNpiVerification` is not known yet.
 *
 * Mounted once, in the root layout, and nowhere else. A host per screen left
 * gaps: a screen that raised the flag without mounting one showed nothing on
 * the tap, and the flag stayed up until the reader reached a screen that did,
 * where the modal then opened unprompted. Two hosts open at once would stack.
 */
export function AbilityGateModal() {
  const userId = useAtomValue(userIdAtom);

  const [showAuthModal, setShowAuthModal] = useAtom(communityShowAuthModalAtom);

  const isOpen = !!showAuthModal;

  const { needsNpiVerification, isLoading } = useAuthStatus();

  const isLeftoverFlag = !!userId && isOpen && !isLoading && !needsNpiVerification;

  useEffect(() => {
    if (isLeftoverFlag) {
      setShowAuthModal(false);
    }
  }, [isLeftoverFlag, setShowAuthModal]);

  if (!userId) {
    return <AuthModal isOpen={isOpen} />;
  }

  return <NpiVerificationModal isOpen={isOpen && needsNpiVerification} />;
}
